import { defineStore } from 'pinia';
import { computed, Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { IOrderDTO, IPaginationDTO, OrderDTO } from '@/dtos';
import { finalizeOrderAPI, getMyOrderAPI, getMyOrdersAPI, saveOrderAPI } from '@/services';
import { useUpdateStatusStore } from './updateStatus';
import { useAuthStore } from './auth';

export const useOrderStore = defineStore('orders', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();
  const authStore = useAuthStore();
  const getOrderByIdQuery = (orderId: Ref<string>) => {
    return useQuery<IOrderDTO, Error>({
      queryKey: computed(() => ['order', orderId]),
      queryFn: async (): Promise<IOrderDTO> => {
        return await getMyOrderAPI(orderId.value);
      },
      enabled: computed(() => !!orderId.value),
    });
  };
  const getMyOrdersQuery = (pagination: Ref<IPaginationDTO>) => {
    return useQuery({
      queryKey: ['orders', pagination.value],
      queryFn: async (): Promise<{ data: IOrderDTO[]; count: number }> => {
        const ret = await getMyOrdersAPI(pagination.value);
        return { data: ret.data.map((order) => new OrderDTO(order)), count: ret.count };
      },
      enabled: computed(() => !!pagination.value),
    });
  };

  const save = useMutation({
    mutationFn: (orderDto: IOrderDTO) => {
      return saveOrderAPI(orderDto);
    },
    onSuccess: () => {
      if (authStore.isUser) {
        queryClient.invalidateQueries({ queryKey: ['my-registration'] });
      }
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`registration`, err);
    },
  });

  const finalizeOrder = useMutation({
    mutationFn: ({ orderId, paymentIntent }: { orderId: string; paymentIntent: string }) => {
      return finalizeOrderAPI(orderId, paymentIntent);
    },
    onSuccess: () => {
      if (authStore.isUser) {
        queryClient.invalidateQueries({ queryKey: ['my-registration'] });
      }
      statusStore.setSuccessStatus(`finalizeOrder`, 'success:update');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`finalizeOrder`, err);
    },
  });

  return {
    getOrderByIdQuery,
    getMyOrdersQuery,
    save,
    finalizeOrder,
  };
});
